import { useCallback, useMemo, useState } from "react";
import { Chip, InputAdornment } from "@mui/material";
import { useTextFieldClearableSupport } from "./useTextFieldClearableSupport";

const addKeys = ["Tab", "Enter", " ", ","];

export const useTextFieldTagsSupport = (
  value: string[],
  onChange: (arg?: string[]) => void
) => {
  const [tagInput, setTagInput] = useState("");

  const addTags = useCallback(
    (tags: string[]) => {
      let newTags = [...new Set(tags.map((d) => d.trim()))];
      newTags = newTags.filter((tag) =>
        value.every((currentTag) => currentTag !== tag)
      );
      newTags = newTags.filter((tag) => tag.trim().length >= 0);
      if (newTags.length > 0) {
        onChange([...value, ...newTags]);
        setTagInput("");
        return true;
      }
      setTagInput("");
      return false;
    },
    [onChange, value]
  );
  const removeTag = useCallback(
    (tag: string) => () => {
      onChange([...value.filter((item: string) => item !== tag)]);
    },
    [onChange, value]
  );

  const clearable = useTextFieldClearableSupport(value.length > 0, () =>
    onChange([])
  );
  const clearableEndAdornment = clearable.slotProps.input.endAdornment;
  const clearableSx = clearable.sx;

  return useMemo(
    () => ({
      value: tagInput,
      onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
        setTagInput(event.target.value),
      onBlur: (
        event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
      ) => {
        if (event.target.value !== "") {
          addTags([event.target.value]);
        }
      },
      onPaste: (event: React.ClipboardEvent<HTMLDivElement>) => {
        event.preventDefault();
        const pastedData = event.clipboardData?.getData("text") || "";
        /* The pasted data is separated only by spaces */
        const newTags = pastedData
          .split(/[\s,;]+/)
          .map((d: string) => d.trim());
        addTags(newTags);
      },
      onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.defaultPrevented) {
          return;
        }
        const empty = tagInput === "";
        const add = addKeys.indexOf(event.key) !== -1;
        if (add) {
          const added = tagInput !== "" ? addTags([tagInput]) : false;
          if (added || (event.key === "Enter" && !empty)) {
            event.preventDefault();
          }
        }
      },
      slotProps: {
        input: {
          startAdornment: (
            <InputAdornment position="start">
              {value?.map((item: string) => (
                <Chip
                  key={item}
                  label={item}
                  color="primary"
                  variant="outlined"
                  onDelete={removeTag(item)}
                  sx={{ fontWeight: 400, mr: 1 }}
                />
              ))}
            </InputAdornment>
          ),
          endAdornment: clearableEndAdornment,
        },
      },
      sx: clearableSx,
    }),
    [addTags, clearableEndAdornment, clearableSx, removeTag, tagInput, value]
  );
};
